import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { CAP, useSession } from "@/auth/session";
import { Chip } from "@/components/Chip";
import { FilePick } from "@/components/FilePick";
import { PageHead } from "@/components/PageHead";
import { Select } from "@/components/inputs";
import { ErrorNote } from "@/components/ErrorNote";
import { products, registry, type RefFileRow, type RefSlotRow } from "@/api/client";
import { fmtBytes } from "@/lib/format";
import { REF_TYPE_DESC, REF_TYPE_LABEL, qk, useProductRefFiles } from "@/modules/products/data";
import { ChoosePrompt, ModelSelect, ProductSelect, ScopeBar, ScopeField, ScopeAction } from "@/modules/products/ScopeBar";
import { useKindParam } from "@/lib/hooks/useKindParam";

/** Reference files — the DID maps and signal sets a bench reads.
 *
 *  THE JOURNEY, in Dattatray's words: *"I should be first able to select
 *  Product -> then either common ref file or for a specific Model -> Then
 *  click upload. Its a journey."* And: *"Again No Pop up just breaks the
 *  UX."*
 *
 *  So the scope bar IS the journey, left to right, and the upload happens
 *  INLINE under the slot it belongs to. A modal was the wrong shape twice
 *  over: it hid the thing being changed behind the form changing it, and it
 *  had to re-ask for the product and model the page already knew.
 *
 *  D-90: a reference file is a DESCRIPTION, so it inherits — common to the
 *  product unless a model has its own, and a company may override either,
 *  per ref_type independently. The four levels resolve most-specific-first;
 *  this page shows one scope at a time so nobody has to guess which file a
 *  bench would actually get.
 *
 *  EVERY UPLOAD IS A NEW VERSION (never an overwrite), which is why the
 *  history is worth showing: it IS the rollback path. Dattatray asked for it
 *  directly — *"where do I see all the reference file per product as well as
 *  per model archived file everything."*
 */
export function ReferenceFilesPage() {
  const { can } = useSession();
  const mayAuthor = can(CAP.MANAGE_PRODUCTS);
  const [kind, setKind] = useKindParam();   // ?kind= arrives from the Products drawer
  const [modelId, setModelId] = useState("");
  const [archived, setArchived] = useState(false);
  const [uploadTo, setUploadTo] = useState<string | null>(null);

  const q = useProductRefFiles(kind, modelId || undefined, archived);

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Products"
        title="Bench reference files"
        desc="What a bench reads to know the ECU: application DIDs, manufacturing DIDs and the signal set. Common to a product, or a model's own."
      />

      <ScopeBar right={
        <ScopeField label="Show">
          <Select value={archived ? "all" : "current"}
            onChange={(e) => setArchived(e.target.value === "all")}>
            <option value="current">Current only</option>
            <option value="all">Include superseded</option>
          </Select>
        </ScopeField>
      }>
        {/* SW-enabled products only (D-95) — a DID map describes software talking to an ECU. */}
        <ProductSelect onlyWithSoftware value={kind} onChange={(k) => { setKind(k); setModelId(""); }} />
        <ModelSelect kind={kind} value={modelId} onChange={setModelId} />
      </ScopeBar>

      {kind && <div className="section-label">Reference files</div>}
      {!kind && <ChoosePrompt what="reference files" />}
      {kind && q.isLoading && <div className="panel panel-pad cap">Loading…</div>}

      <div style={{ display: "grid", gap: 10 }}>
        {(kind ? q.data?.slots ?? [] : []).map((slot) => (
          <Slot key={slot.ref_type} kind={kind} modelId={modelId} slot={slot}
            mayAuthor={mayAuthor}
            open={uploadTo === slot.ref_type}
            onToggle={() => setUploadTo(uploadTo === slot.ref_type ? null : slot.ref_type)} />
        ))}
      </div>
    </div>
  );
}

function FileLine({ f, tone, label }: { f: RefFileRow; tone: "ok" | "warn" | "mute"; label: string }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "2px 0" }}>
      <Chip tone={tone}>{label} · v{f.version}</Chip>
      <a href={f.download_url} style={{ fontFamily: "var(--mono)" }}>{f.filename}</a>
      <span className="cap">{fmtBytes(f.size_bytes)}</span>
      {f.created_at && <span className="cap">{f.created_at.slice(0, 10)}</span>}
    </div>
  );
}

function Slot({ kind, modelId, slot, mayAuthor, open, onToggle }: {
  kind: string; modelId: string; slot: RefSlotRow; mayAuthor: boolean;
  open: boolean; onToggle: () => void;
}) {
  return (
    <div className="panel panel-pad">
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        {/* House grammar: bold name, mono secondary id — the same pair every
            Drawer header and the permission map use. `<strong>` + `<code>`
            renders at a different weight and size. */}
        <span style={{ fontWeight: 700 }}>
          {REF_TYPE_LABEL[slot.ref_type] ?? slot.ref_type}
        </span>
        <span className="mono cap">{slot.ref_type}</span>
        {mayAuthor && (
          <button className={open ? "btn ghost sm" : "btn teal sm"}
            style={{ marginLeft: "auto" }} onClick={onToggle}>
            {open ? "Cancel" : "Upload new version"}
          </button>
        )}
      </div>
      <div className="cap" style={{ marginTop: 4 }}>{REF_TYPE_DESC[slot.ref_type] ?? ""}</div>

      <div style={{ marginTop: 10 }}>
        {slot.current
          ? <FileLine f={slot.current} tone="ok"
              label={modelId ? "this model" : "common"} />
          : <Chip tone="warn">nothing uploaded for this scope</Chip>}
      </div>

      {slot.overrides.length > 0 && (
        <div style={{ marginTop: 6 }}>
          {slot.overrides.map((o) => (
            <FileLine key={o.file_id} f={o} tone="warn"
              label={`override · ${o.company_id}`} />
          ))}
        </div>
      )}

      {slot.history.length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary className="cap" style={{ cursor: "pointer" }}>
            {slot.history.length} superseded version{slot.history.length === 1 ? "" : "s"}
            {" — kept because rolling back means pointing at one again"}
          </summary>
          <div style={{ marginTop: 6 }}>
            {slot.history.map((h) => (
              <FileLine key={h.file_id} f={h} tone="mute"
                label={h.archived ? "archived" : "superseded"} />
            ))}
          </div>
        </details>
      )}

      {open && <UploadRow kind={kind} modelId={modelId} refType={slot.ref_type}
        onDone={onToggle} />}
    </div>
  );
}

/** The upload, INLINE, under the slot it fills. It inherits the product and
 *  model from the scope bar instead of re-asking, which is the whole reason a
 *  modal was wrong here: the journey had already answered those questions. */
function UploadRow({ kind, modelId, refType, onDone }: {
  kind: string; modelId: string; refType: string; onDone: () => void;
}) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [company, setCompany] = useState("");

  const companies = useQuery({
    queryKey: ["registry", "companies"], queryFn: registry.companies, staleTime: 60_000,
  });

  const up = useMutation({
    mutationFn: () => products.uploadRefFile(kind, file as File, refType, {
      company_id: company || undefined, model_id: modelId || undefined,
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["products", kind, "ref-files"] });
      void qc.invalidateQueries({ queryKey: qk.products });
      onDone();
    },
  });

  return (
    <div style={{
      marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--line)",
      display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap",
    }}>
      <ScopeField label="File">
        <FilePick accept=".xlsx,.xls,.csv,.desc,.json" label="Choose file"
          fileName={file?.name ?? ""} onFile={setFile} />
      </ScopeField>

      <ScopeField label="Scope">
        <Select value={company} onChange={(e) => setCompany(e.target.value)}
          disabled={companies.isLoading} style={{ minWidth: 260 }}>
          <option value="">
            Standard — {modelId ? "this model" : "this product"}, every company
          </option>
          {(companies.data?.companies ?? []).map((c) => (
            <option key={c.company_id} value={c.company_id}>
              Override for {c.name}
            </option>
          ))}
        </Select>
      </ScopeField>

      <ScopeAction>
        <button className="btn teal sm" disabled={!file || up.isPending}
          onClick={() => up.mutate()}>
          {up.isPending ? "Uploading…" : "Upload as new version"}
        </button>
      </ScopeAction>

      <ErrorNote error={up.error} />
    </div>
  );
}
